import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft,
    Building2,
    Phone,
    Mail,
    MapPin,
    FileText,
    DollarSign,
    Package,
    Download,
    Eye,
    Edit,
    MessageSquare,
    CheckCircle2,
    Clock,
    History,
    RefreshCw,
    Search,
    X,
} from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/formatters";

import { ErrorState } from "@/components/shared/ErrorState";
import { TooltipWrapper } from "@/components/shared/TooltipWrapper";
import { AnimatedContainer } from "@/components/shared/AnimatedContainer";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { boletosApi, Client, clientsApi, Order, ordersApi, BoletoTransaction } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { DateRange } from "react-day-picker";
import { startOfYear, endOfYear } from "date-fns";
import { Boleto } from "./Boleto";
import { Input } from "@/components/ui/input";

import { ClientDetailsSkeleton } from "./component/ClientDetailsSkeleton";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { FinancialTableView } from "@/components/Financial/FinancialTableView";
import { useFinancialData } from "@/hooks/useFinancialData";
import { useBoletoViewer } from "@/hooks/useBoletoViewer";
import { buildBoletoParcelasFromTransaction } from "@/utils/financialUtils";
import { isClientCnpjChangeEnabled } from "@/features/clientCnpjChangeFeature";
import { ChangeCnpjDialog } from "./component/ChangeCnpjDialog";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { AddressList } from "@/components/shared/address/AddressList";
import { ClientCustomerGroupField } from "./component/ClientCustomerGroupField";

const ClientDetails = () => {
    const { clientCode } = useParams<{ clientCode: string }>();

    const navigate = useNavigate();
    const { confirm, ConfirmDialog } = useConfirmDialog();

    // Estados de loading
    const [ error, setError ] = useState<string | null>( null );

    // Estados para tabs
    const [ activeTab, setActiveTab ] = useState( "overview" );

    // Estados para modais
    const [ addNoteDialogOpen, setAddNoteDialogOpen ] = useState( false );
    const [ editStatusDialogOpen, setEditStatusDialogOpen ] = useState( false );
    const [ selectedOrder, setSelectedOrder ] = useState<Order | null>( null );
    const [ noteText, setNoteText ] = useState( "" );
    const [ newStatus, setNewStatus ] = useState<Order[ "status" ]>( "pending" );
    const [ searchQuery, setSearchQuery ] = useState( "" );
    const [ changeCnpjOpen, setChangeCnpjOpen ] = useState( false );

    const clientQueryKey = useMemo( () => [ 'client', clientCode ] as const, [ clientCode ] );
    const {
        data: clientData,
        isLoading: isLoadingClient,
    } = useQuery<Client>( {
        queryKey: clientQueryKey,
        queryFn: async () => await clientsApi.getByCode( clientCode ) || null,
        enabled: !!clientCode
    } );

    const {
        data: orderData,
        isLoading: isLoadingOrder,
    } = useQuery<Order[]>( {
        queryKey: [ 'order', clientCode ],
        queryFn: async () => {
            const { data } = await ordersApi.getByClient( clientCode );
            return data || [];
        },
        enabled: !!clientCode,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
    } );

    const hoje = useMemo( () => new Date(), [] );
    const sortedOrders = useMemo( () => {
        if ( !orderData ) return [];
        let filtered = [ ...orderData ];

        if ( searchQuery.trim() ) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter( order =>
                ( order.orderNumber && String( order.orderNumber ).toLowerCase().includes( query ) ) ||
                ( order.id && String( order.id ).toLowerCase().includes( query ) ) ||
                ( order.clientName && String( order.clientName ).toLowerCase().includes( query ) )
            );
        }

        return filtered.sort( ( a, b ) =>
            new Date( b.createdAt ).getTime() - new Date( a.createdAt ).getTime()
        );
    }, [ orderData, searchQuery ] );

    const [ date, setDate ] = useState<DateRange | undefined>( {
        from: startOfYear( hoje ),
        to: endOfYear( hoje )
    } );

    const {
        data: financialData,
        isLoading: isLoadingBoleto,
        refetch: refetchFinancialData,
    } = useFinancialData( clientData?.clientCnpj, date );
    const { boleto, currentGeneratingBoletoId, unavailableBoletoIds, openBoleto, closeBoleto } = useBoletoViewer( {
        onUnavailable: () => refetchFinancialData(),
    } );

    const transactions = financialData?.customerTransactionsList || [];
    const summary = financialData?.summary || { total: 0, liquidado: 0, vencidos: 0, emAberto: 0 };

    const handleGenerateBoleto = ( transaction: BoletoTransaction ) =>
        openBoleto( buildBoletoParcelasFromTransaction( transaction ) );

    // Top 5 produtos contratados
    const topProducts = useMemo( () => {
        if ( !orderData ) return [];

        const data = [ ...orderData ].sort( ( a, b ) => b.totalAmount - a.totalAmount )

        return data.slice( 0, 5 ).map( item => {
            return {
                code: item.orderNumber,
                data: item.createdAt,
                quantity: item.items.reduce( ( total, item ) => total + item.quantityContracted, 0 ),
                total: item.totalAmount,
            }
        } );
    }, [ orderData ] );

    // Handlers
    const handleGoBack = () => window.history.back()

    const handleAddNote = () => {
        setNoteText( "" );
        setAddNoteDialogOpen( false );
    };

    const handleEditOrderStatus = async () => {
        if ( !selectedOrder ) return;

        const confirmed = await confirm( {
            title: "Confirmar alteração de status",
            description: `Deseja realmente alterar o status do pedido ${ selectedOrder.orderNumber } para ${ getOrderStatusLabel( newStatus ) }?`,
            confirmLabel: "Alterar Status",
        } );

        if ( confirmed ) {
            setEditStatusDialogOpen( false );
            setSelectedOrder( null );
        }
    };


    const getOrderStatusLabel = ( status: Order[ "status" ] ): string => {
        switch ( status ) {
            case "pending": return "Criado";
            case "created": return "Criado";
            case "processing": return "Em Processamento";
            case "delivered": return "Entregue";
            case "cancelled": return "Cancelado";
            default: return status;
        }
    };


    const getStatusVariant = ( status: Order[ 'status' ] ): "default" | "secondary" | "destructive" | "outline" | "success" => {
        const variants = {
            delivered: "success" as const,
            processed: "default" as const,
            canceled: "destructive" as const,
            pending: "outline" as const,
        };
        return variants[ status ] || "outline";
    };

    const getStatusLabel = ( status: Order[ 'status' ] ): string => {
        const labels = {
            pending: "Criado",
            created: "Criado",
            processed: "Processado",
            delivered: "Entregue",
            canceled: "Cancelado",
        };
        return labels[ status ] || status;
    };

    const { metrics, clientsForFilter } = useMemo( () => {
        const initial = {
            metrics: { pending: 0, processing: 0, canceled: 0, delivered: 0 },
            clientMap: new Map<string, { name: string; clientCode: string }>()
        };

        if ( !orderData ) {
            return { metrics: initial.metrics, clientsForFilter: [] };
        }

        const result = orderData.reduce(
            ( acc, order ) => {
                // Calcula métricas
                if ( order.status === "pending" || order.status === "created" ) acc.metrics.pending++;
                if ( order.status === "processed" ) acc.metrics.processing++;
                if ( order.status === "canceled" ) acc.metrics.canceled++;
                if ( order.status === "delivered" ) acc.metrics.delivered++;

                // Cria lista de clientes únicos
                if ( !acc.clientMap.has( order.clientCode ) ) {
                    acc.clientMap.set( order.clientCode, {
                        name: order.clientName,
                        clientCode: order.clientCode,
                    } );
                }
                return acc;
            },
            initial
        );

        const clients = Array.from( result.clientMap.values() ).sort( ( a, b ) => a.name.localeCompare( b.name ) );

        return { metrics: result.metrics, clientsForFilter: clients };
    }, [ orderData ] );

    const [ regeneratingId, setRegeneratingId ] = useState<string | null>( null );
    const { regeneratePDF } = useRegeneratePDF();

    // Simular download de PDF
    const handleDownloadPDF = async ( orderNumber: string ) => {
        setRegeneratingId( orderNumber );
        try {
            const orderItem = orderData?.find( ( o ) => o.id === orderNumber );
            if ( !orderItem ) {
                toast.error( 'Erro', { description: "Pedido não encontrado" } );
                return;
            }

            await regeneratePDF( orderItem );
        } finally {
            setRegeneratingId( null );
        }
    };

    // Loading state
    if ( isLoadingClient ) {
        return <ClientDetailsSkeleton />;
    }

    // Error state
    if ( error ) {
        return (
            <div className="min-h-screen bg-muted/30 dark:bg-background">
                <header className="border-b bg-foreground text-background p-6">
                    <h1 className="text-2xl font-bold">Detalhes do Cliente</h1>
                </header>
                <main className="container mx-auto px-6 py-8">
                    <ErrorState
                        message="Não foi possível carregar os dados do cliente."
                        onRetry={() => {
                            setError( null );
                        }}
                        onGoHome={() => navigate( "/backoffice/clientes" )}
                        details={error}
                    />
                </main>
            </div>
        );
    }

    // Validação - Cliente não encontrado
    if ( !clientData ) {
        return (
            <div className="min-h-screen bg-muted/30 dark:bg-background">
                <main className="container mx-auto px-6 py-8">
                    <EmptyState
                        title="Cliente não encontrado"
                        message="Verifique o código do cliente e tente novamente."
                        className="py-12"
                    />
                    <div className="flex justify-center mt-6">
                        <Button variant="outline" onClick={handleGoBack}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Voltar para Lista de Clientes
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <>
            <ConfirmDialog />
            <div className="min-h-screen bg-muted/30 dark:bg-background">
                {/* Header */}
                <header className="border-b bg-foreground text-background">
                    <div className="container mx-auto px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Button variant="ghost" size="sm" onClick={handleGoBack} className="hover:bg-muted">
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Voltar
                                </Button>
                                <Separator orientation="vertical" className="h-8 bg-muted" />
                                <div>
                                    <h1 className="text-2xl font-bold">{clientData.clientCompanyName}</h1>
                                    <p className="text-sm text-muted-foreground">
                                        Código: {clientData.clientCodeCnx}
                                    </p>
                                </div>
                            </div>
                            {isClientCnpjChangeEnabled && (
                                <Button variant="secondary" onClick={() => setChangeCnpjOpen( true )}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Trocar CNPJ
                                </Button>
                            )}
                        </div>
                    </div>
                </header>

                <main className="container mx-auto px-6 py-8">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <TabsList className={`grid w-full ${ isCustomerGroupAddressEnabled ? "grid-cols-4" : "grid-cols-3" }`}>
                            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                            <TabsTrigger value="orders">Pedidos</TabsTrigger>
                            <TabsTrigger value="financial">Financeiro</TabsTrigger>
                            {isCustomerGroupAddressEnabled && (
                                <TabsTrigger value="addresses">Endereços</TabsTrigger>
                            )}
                        </TabsList>

                        {isCustomerGroupAddressEnabled && (
                            <TabsContent value="addresses" className="space-y-6">
                                <AddressList clientId={clientData.clientId} clientQueryKey={clientQueryKey} />
                            </TabsContent>
                        )}

                        {/* TAB 1 - Visão Geral */}
                        <TabsContent value="overview" className="space-y-6">
                            {/* Dados Cadastrais */}
                            <Card className="border-border dark:border-border shadow-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="h-5 w-5" />
                                        Dados Cadastrais
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid gap-6 md:grid-cols-2">
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Nome da Escola</Label>
                                                <p className="font-medium">{clientData.clientFantasyName}</p>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Código do Cliente</Label>
                                                <p className="font-mono font-medium">{clientData.clientCodeCnx}</p>
                                            </div>
                                            {isCustomerGroupAddressEnabled ? (
                                                <ClientCustomerGroupField client={clientData} clientQueryKey={clientQueryKey} />
                                            ) : (
                                                <div>
                                                    <Label className="text-sm text-muted-foreground">Grupo do Cliente</Label>
                                                    <p className="font-mono font-medium">{clientData.clientCustomerGroup}</p>
                                                </div>
                                            )}
                                            <div>
                                                <Label className="text-sm text-muted-foreground">E-mail</Label>
                                                <p className="font-medium">{clientData.clientEmail}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Telefone</Label>
                                                <p className="font-medium">{clientData.clientPhone}</p>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Endereço</Label>
                                                <p className="font-medium text-sm">
                                                    {clientData.clientAddress}, {` `}
                                                    {clientData.clientNumber} {` `}<br />
                                                    {clientData.clientCity}/{clientData.clientState}{` `}
                                                    CEP: {clientData.clientPostCode} <br />
                                                    {clientData.clientComplement}
                                                </p>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">CNPJ</Label>
                                                <p className="font-mono font-medium">{clientData.clientCnpj}</p>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Frete</Label>
                                                <p className="font-mono font-medium">{clientData.shippingFee}%</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Métricas do Cliente */}
                            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                                <AnimatedContainer variant="scale" delay={0}>
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                                    Total de Pedidos
                                                </CardTitle>
                                                <TooltipWrapper content="Número total de pedidos realizados">
                                                    <Package className="h-4 w-4 text-blue-600" />
                                                </TooltipWrapper>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="text-3xl font-bold">{orderData?.length || 0}</div>
                                            <p className="text-xs text-muted-foreground mt-1">Pedidos Realizados</p>
                                        </CardContent>
                                    </Card>
                                </AnimatedContainer>
                                <AnimatedContainer variant="scale" delay={0}>
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                                    Valor Total Faturado
                                                </CardTitle>
                                                <DollarSign className="h-4 w-4 text-green-600" />
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="text-3xl font-bold text-green-600">
                                                {formatCurrency( orderData?.reduce( ( acumulador, item ) => acumulador + item.totalAmount, 0 ) )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">Lifetime value</p>
                                        </CardContent>
                                    </Card>
                                </AnimatedContainer>
                            </div>

                            {/* Top 5 Produtos Contratados */}
                            <Card className="border-border dark:border-border shadow-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="h-5 w-5" />
                                        Top 5 Produtos Contratados
                                    </CardTitle>
                                    <CardDescription>Produtos mais pedidos por este cliente</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Código</TableHead>
                                                <TableHead>Data</TableHead>
                                                <TableHead className="text-center">Quantidade</TableHead>
                                                <TableHead className="text-right">Valor Total</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {topProducts?.length > 0 ? (
                                                topProducts?.map( ( product ) => (
                                                    <TableRow key={product.code}>
                                                        <TableCell className="font-mono text-sm">{product.code}</TableCell>
                                                        <TableCell>{formatDate( new Date( product.data ) )}</TableCell>
                                                        <TableCell className="text-center font-medium">{product.quantity}</TableCell>
                                                        <TableCell className="text-right font-medium">
                                                            {formatCurrency( product.total )}
                                                        </TableCell>
                                                    </TableRow>
                                                ) )
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <EmptyState
                                                            title="Nenhum produto encontrado"
                                                            message="Este cliente ainda não possui produtos registrados."
                                                            className="py-8"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* TAB 2 - Pedidos */}
                        <TabsContent value="orders" className="space-y-6">
                            <Card className="border-border dark:border-border shadow-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="h-5 w-5" />
                                        Pedidos do Cliente
                                    </CardTitle>
                                    <CardDescription>
                                        {orderData?.length} pedido(s) no total
                                    </CardDescription>
                                </CardHeader>

                                <div className="px-6 mb-4">
                                    <div className="relative w-full md:w-[350px]">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Buscar pedido por número..."
                                            value={searchQuery}
                                            onChange={( e ) => setSearchQuery( e.target.value )}
                                            className="pl-9 pr-9"
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery( "" )}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="grid px-6 mb-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <AnimatedContainer variant="scale" delay={0}>
                                        <Card className="bg-muted border-border">
                                            <CardContent className="pt-4 pb-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-foreground">Criados</p>
                                                        <p className="text-2xl font-bold text-yellow-500">{metrics.pending}</p>
                                                    </div>
                                                    <Clock className="h-8 w-8 text-yellow-500/50" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </AnimatedContainer>
                                    <AnimatedContainer variant="scale" delay={0}>
                                        <Card className="bg-muted border-border">
                                            <CardContent className="pt-4 pb-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-foreground">Processados</p>
                                                        <p className="text-2xl font-bold text-blue-500">{metrics.processing}</p>
                                                    </div>
                                                    <Package className="h-8 w-8 text-blue-500/50" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </AnimatedContainer>
                                    <AnimatedContainer variant="scale" delay={0}>
                                        <Card className="bg-muted border-border">
                                            <CardContent className="pt-4 pb-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-foreground">Entregues</p>
                                                        <p className="text-2xl font-bold text-green-500">{metrics.delivered}</p>
                                                    </div>
                                                    <CheckCircle2 className="h-8 w-8 text-green-500/50" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </AnimatedContainer>
                                    <AnimatedContainer variant="scale" delay={0}>
                                        <Card className="bg-muted border-border">
                                            <CardContent className="pt-4 pb-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-foreground">Cancelados</p>
                                                        <p className="text-2xl font-bold text-red-500">{metrics.canceled}</p>
                                                    </div>
                                                    <History className="h-8 w-8 text-red-500/50" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </AnimatedContainer>
                                </div>

                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>ID Pedido</TableHead>
                                                <TableHead>Data</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Valor</TableHead>
                                                <TableHead className="text-right">Ações</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {sortedOrders.length > 0 ? (
                                                sortedOrders.map( ( order ) => {

                                                    return (
                                                        <TableRow
                                                            key={order.orderNumber}
                                                        >
                                                            <TableCell className="font-medium">{order.orderNumber}</TableCell>
                                                            <TableCell>{formatDate( new Date( order.createdAt ) )}</TableCell>
                                                            <TableCell>
                                                                <Badge variant={getStatusVariant( order.status )}>
                                                                    {getStatusLabel( order.status )}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="text-right font-medium">
                                                                {formatCurrency( order.totalAmount )}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        asChild
                                                                        title="Ver detalhes"
                                                                    >
                                                                        <Link to={`/backoffice/pedidos/${ order.id }`}>
                                                                            <Eye className="h-4 w-4" />
                                                                        </Link>
                                                                    </Button>
                                                                    <TooltipWrapper content="Baixar PDF do pedido">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleDownloadPDF( order.id )}
                                                                            disabled={regeneratingId === order.id}
                                                                        >
                                                                            {regeneratingId === order.id ? (
                                                                                <RefreshCw className="h-4 w-4 animate-spin" />
                                                                            ) : (
                                                                                <Download className="h-4 w-4" />
                                                                            )}
                                                                        </Button>
                                                                    </TooltipWrapper>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                } )
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5}>
                                                        <EmptyState
                                                            title="Nenhum pedido encontrado"
                                                            message="Este cliente ainda não possui pedidos registrados."
                                                            className="py-8"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="financial" className="space-y-6">
                            <FinancialTableView
                                transactions={transactions}
                                summary={summary}
                                isLoading={isLoadingBoleto}
                                onGenerateBoleto={handleGenerateBoleto}
                                tableTitle="Gestão de Parcelas"
                                isBackoffice={true}
                                date={date}
                                setDate={setDate}
                                currentGeneratingBoletoId={currentGeneratingBoletoId}
                                unavailableBoletoIds={unavailableBoletoIds}
                            />
                        </TabsContent>
                    </Tabs>
                </main>

                <Dialog open={addNoteDialogOpen} onOpenChange={setAddNoteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Adicionar Observação</DialogTitle>
                            <DialogDescription>
                                Registre uma observação sobre este cliente
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="note">Observação</Label>
                                <Textarea
                                    id="note"
                                    placeholder="Digite a observação..."
                                    value={noteText}
                                    onChange={( e ) => setNoteText( e.target.value )}
                                    rows={4}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddNoteDialogOpen( false )}>
                                Cancelar
                            </Button>
                            <Button onClick={handleAddNote} disabled={!noteText.trim()}>
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Adicionar
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={editStatusDialogOpen} onOpenChange={setEditStatusDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Editar Status do Pedido</DialogTitle>
                            <DialogDescription>
                                Pedido: {selectedOrder?.orderNumber}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="status">Novo Status</Label>
                                <Select value={newStatus} onValueChange={( value ) => setNewStatus( value as Order[ "status" ] )}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Criado</SelectItem>
                                        <SelectItem value="processing">Em Processamento</SelectItem>
                                        <SelectItem value="delivered">Entregue</SelectItem>
                                        <SelectItem value="cancelled">Cancelado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditStatusDialogOpen( false )}>
                                Cancelar
                            </Button>
                            <Button onClick={handleEditOrderStatus}>
                                <Edit className="h-4 w-4 mr-2" />
                                Atualizar Status
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {isClientCnpjChangeEnabled && clientData && (
                    <ChangeCnpjDialog
                        source={clientData}
                        open={changeCnpjOpen}
                        onOpenChange={setChangeCnpjOpen}
                        onSuccess={(target) => navigate(`/backoffice/client/${ target.clientCodeCnx }`)}
                    />
                )}

                {!!clientData && !!boleto && !!date && (
                    <Boleto
                        installment={boleto}
                        cnpj={clientData.clientCnpj}
                        date={date}
                        setInstallment={closeBoleto}
                    />
                )}
            </div>
        </>
    );
};

export default ClientDetails;
